import { AppError } from '../../domain/errors';
import type { Clock } from '../../application/ports/clock';
import type { Lock } from '../../application/ports/lock';
import type {
  SessionPort,
  SessionRefresher,
  SessionSnapshot,
  SessionStore,
  StoredSession,
} from '../../application/ports/session';

/** A token this close to its expiry is renewed before the request goes out. */
const DEFAULT_REFRESH_SKEW_MS = 15_000;
/** Name of the cross-tab lock, so only one tab of one account refreshes at a time. */
const REFRESH_LOCK = 'session-refresh';

export interface AuthSessionOptions {
  store: SessionStore;
  refresher: SessionRefresher;
  clock: Clock;
  /** Cross-tab (browser) or process-local (mobile, desktop) mutual exclusion. */
  lock: Lock;
  refreshSkewMs?: number;
  /** Called whenever the session starts, is renewed or ends (`null`). */
  onChange?: (session: SessionSnapshot | null) => void;
}

/**
 * Keeps one signed-in session alive: single-flight refresh, one refresh per
 * browser at a time, and a hard stop when the gateway ends the session
 * (ADR 0006, ADR 0007). Whether the refresh token travels in a cookie or in the
 * request body is the {@link SessionRefresher}'s business.
 */
export class AuthSession implements SessionPort {
  private current: StoredSession | null = null;
  /** Rebuilt only when the session changes, so `useSyncExternalStore` sees a stable value. */
  private cachedSnapshot: SessionSnapshot | null = null;
  private loaded = false;
  private inFlight: Promise<StoredSession> | null = null;
  private readonly listeners = new Set<(session: SessionSnapshot | null) => void>();

  constructor(private readonly options: AuthSessionOptions) {
    if (options.onChange) this.listeners.add(options.onChange);
  }

  /** The session as the UI sees it, or null when signed out. The same object until it changes. */
  snapshot(): SessionSnapshot | null {
    return this.cachedSnapshot;
  }

  /** Notifies on every session change; returns the unsubscribe function. */
  subscribe(listener: (session: SessionSnapshot | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Stores the session a sign-in just produced. */
  async start(session: StoredSession): Promise<void> {
    this.setCurrent(session);
    this.loaded = true;
    await this.options.store.write(session);
    this.emit();
  }

  /** Forgets the session locally; telling the gateway is the caller's job. */
  async end(): Promise<void> {
    this.setCurrent(null);
    this.loaded = true;
    await this.options.store.clear();
    this.emit();
  }

  /**
   * Restores a session kept from an earlier run (mobile) or renews one that
   * only the cookie proves (browser reload). Returns null when there is none.
   */
  async restore(): Promise<SessionSnapshot | null> {
    const stored = await this.read();
    if (stored && !this.isStale(stored)) return snapshotOf(stored);
    try {
      return snapshotOf(await this.renew());
    } catch (error) {
      const appError = AppError.from(error);
      if (appError.terminatesSession || appError.code === 'UNAUTHORIZED') return null;
      throw appError;
    }
  }

  async authHeaders(options: { forceRefresh?: boolean } = {}): Promise<Record<string, string>> {
    const stored = await this.read();
    if (!stored) throw new AppError('UNAUTHORIZED', 'There is no session');
    if (!options.forceRefresh && !this.isStale(stored))
      return { Authorization: `Bearer ${stored.accessToken}` };
    const fresh = await this.renew({ force: options.forceRefresh ?? false });
    return { Authorization: `Bearer ${fresh.accessToken}` };
  }

  async refresh(): Promise<void> {
    await this.renew();
  }

  onTerminated(error: AppError): void {
    this.setCurrent(null);
    this.loaded = true;
    void this.options.store.clear();
    this.emit(error);
  }

  /** One refresh at a time in this client, and one across the browser's tabs. */
  private renew(options: { force?: boolean } = {}): Promise<StoredSession> {
    this.inFlight ??= this.options.lock
      .run(REFRESH_LOCK, () => this.renewExclusively(options.force ?? false))
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }

  private async renewExclusively(force: boolean): Promise<StoredSession> {
    // Another tab may have refreshed while we waited for the lock.
    const stored = await this.options.store.read();
    if (!force && stored && !this.isStale(stored)) {
      this.setCurrent(stored);
      this.emit();
      return stored;
    }
    try {
      const fresh = await this.options.refresher.refresh(stored ?? this.current);
      this.setCurrent(fresh);
      this.loaded = true;
      await this.options.store.write(fresh);
      this.emit();
      return fresh;
    } catch (error) {
      const appError = AppError.from(error);
      if (appError.terminatesSession || appError.code === 'UNAUTHORIZED') {
        this.setCurrent(null);
        await this.options.store.clear();
        this.emit(appError);
      }
      throw appError;
    }
  }

  private async read(): Promise<StoredSession | null> {
    if (!this.loaded) {
      this.setCurrent(await this.options.store.read());
      this.loaded = true;
    }
    return this.current;
  }

  /** The one place the session changes, so the snapshot stays a stable object. */
  private setCurrent(session: StoredSession | null): void {
    this.current = session;
    this.cachedSnapshot = session ? snapshotOf(session) : null;
  }

  private isStale(session: StoredSession): boolean {
    const skew = this.options.refreshSkewMs ?? DEFAULT_REFRESH_SKEW_MS;
    return new Date(session.accessTokenExpiresAt).getTime() - skew <= this.options.clock.now();
  }

  private emit(_reason?: AppError): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}

function snapshotOf(session: StoredSession): SessionSnapshot {
  return {
    accountId: session.accountId,
    email: session.email,
    accessTokenExpiresAt: session.accessTokenExpiresAt,
  };
}
